import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { Modal } from '../components/ui/Modal';
import { Field } from '../components/ui/Field';
import { Listings as ListingsApi, type Listing } from '../api/client';
import { RefreshCw, Search, Plus, Filter, ExternalLink, Trash2, Package } from 'lucide-react';
import { ListingEditModal } from '../components/widgets/ListingEditModal';
import { useStore } from '../store';
import { motion } from 'framer-motion';

export function Listings() {
  const [listings, setListings] = useState<Listing[] | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState<Listing | null>(null);
  const [creating, setCreating] = useState(false);
  const pushToast = useStore((s) => s.pushToast);

  const load = () => ListingsApi.list().then(setListings).catch(() => setListings([]));
  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await ListingsApi.sync();
      pushToast({ kind: 'success', message: `Synced ${r.synced} listings from Etsy` });
      await load();
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (l: Listing) => {
    if (!confirm(`Delete "${l.title}" from Etsy? This cannot be undone.`)) return;
    try {
      await ListingsApi.remove(l.id);
      pushToast({ kind: 'success', message: 'Deleted from Etsy' });
      await load();
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    }
  };

  const filtered = (listings ?? [])
    .filter((l) => filter === 'all' || l.status === filter)
    .filter((l) => !search || (l.title ?? '').toLowerCase().includes(search.toLowerCase()));

  const counts = {
    all: listings?.length ?? 0,
    active: listings?.filter((l) => l.status === 'active').length ?? 0,
    draft: listings?.filter((l) => l.status === 'draft').length ?? 0,
    inactive: listings?.filter((l) => l.status === 'inactive').length ?? 0,
  };

  return (
    <div>
      <PageHeader
        kicker="// listings · 02"
        title="Your Etsy inventory"
        description="Browse, edit, and push to Etsy. Filter by status, search by title, bulk-sync."
        actions={
          <>
            <Button variant="secondary" leftIcon={<RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />} onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync from Etsy'}
            </Button>
            <Button variant="primary" leftIcon={<Plus size={13} />} onClick={() => setCreating(true)}>
              New listing
            </Button>
          </>
        }
      />

      {/* Filter bar */}
      <Card className="mb-4 p-3" crisp>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-1 bg-cream-100 rounded-xl p-0.5 border border-cream-300">
            {(['all', 'active', 'draft', 'inactive'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={
                  'px-3 h-7 text-[11.5px] font-medium rounded-lg transition flex items-center gap-1.5 ' +
                  (filter === s ? 'bg-white text-ink-800 shadow-[0_1px_2px_rgba(80,50,20,0.06)] border border-cream-300' : 'text-ink-500 hover:text-ink-800')
                }
              >
                {s}
                <span className="kicker text-[9.5px] opacity-70">{counts[s as keyof typeof counts]}</span>
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" leftIcon={<Filter size={12} />} onClick={() => pushToast({ kind: 'info', message: 'Advanced filters coming soon' })}>
            More
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="grid grid-cols-[44px_1fr_120px_90px_90px_120px_44px] px-4 py-3 border-b border-cream-300 kicker bg-cream-50/40">
          <div></div>
          <div>Title</div>
          <div>Price</div>
          <div>Qty</div>
          <div>Status</div>
          <div className="text-right">Etsy ID</div>
          <div></div>
        </div>

        {listings === null
          ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="px-4 py-3"><Skeleton className="h-8" /></div>)
          : filtered.length === 0
            ? <EmptyState filter={filter} />
            : filtered.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  onClick={() => setEditing(l)}
                  className="grid grid-cols-[44px_1fr_120px_90px_90px_120px_44px] px-4 py-3 border-b border-cream-200 hover:bg-cream-50/60 transition group items-center cursor-pointer"
                >
                  <div className="h-8 w-8 rounded-lg bg-cream-100 border border-cream-300 overflow-hidden flex items-center justify-center">
                    {(l as any).thumbnail_url ? (
                      <img src={(l as any).thumbnail_url} alt={l.title ?? ''} className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <Package size={12} className="text-ink-400" />
                    )}
                  </div>
                  <div className="min-w-0 pr-3">
                    <div className="text-[13px] text-ink-800 truncate tracking-tight font-medium">{l.title ?? '—'}</div>
                    <div className="kicker mt-0.5 text-ink-400">LST · {l.id}</div>
                  </div>
                  <div className="text-[12.5px] tabular-nums text-ink-800">${l.price_amount ?? '—'}</div>
                  <div className="text-[12.5px] tabular-nums text-ink-700">{l.quantity ?? '—'}</div>
                  <div>
                    <Badge tone={l.status === 'active' ? 'success' : l.status === 'draft' ? 'warn' : 'neutral'}>{l.status}</Badge>
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="id-chip">{l.etsy_listing_id ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {l.etsy_listing_id && (
                      <a
                        href={`https://www.etsy.com/listing/${l.etsy_listing_id}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="p-1 rounded-md text-ink-400 hover:text-etsy transition"
                        title="Open on Etsy"
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                    <button
                      onClick={() => handleDelete(l)}
                      className="p-1 rounded-md text-ink-400 hover:text-rose-500 transition"
                      title="Delete from Etsy"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </motion.div>
              ))}
      </Card>

      {editing && (
        <ListingEditModal
          listing={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      {creating && (
        <NewListingModal
          onClose={() => setCreating(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function EmptyState({ filter }: { filter: string }) {
  return (
    <div className="px-4 py-16 text-center">
      <div className="inline-flex h-12 w-12 rounded-2xl bg-cream-200 border border-cream-300 items-center justify-center mb-3">
        <Search size={18} className="text-ink-500" />
      </div>
      <div className="text-[13px] text-ink-800 mb-1 font-medium">No {filter !== 'all' ? filter : ''} listings</div>
      <div className="text-[12px] text-ink-500">Try changing filters, or generate a new draft from the studio.</div>
    </div>
  );
}

function NewListingModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const pushToast = useStore((s) => s.pushToast);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('17.99');
  const [quantity, setQuantity] = useState('10');
  const [tags, setTags] = useState('');
  const [whoMade, setWhoMade] = useState<'i_did' | 'collective' | 'someone_else'>('i_did');
  const [whenMade, setWhenMade] = useState<'made_to_order' | '2020_2024' | '2010_2019' | '2000_2009' | '1990s' | 'before_1990'>('made_to_order');
  const [isDigital, setIsDigital] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!title.trim()) {
      pushToast({ kind: 'error', message: 'Title is required' });
      return;
    }
    const priceNum = parseFloat(price);
    const quantityNum = parseInt(quantity, 10);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      pushToast({ kind: 'error', message: 'Price must be a non-negative number' });
      return;
    }
    setSaving(true);
    try {
      const r = await ListingsApi.create({
        title: title.trim(),
        description,
        price_amount: priceNum,
        quantity: quantityNum,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 13),
        who_made: whoMade,
        when_made: whenMade,
        is_digital: isDigital,
      } as any);
      const persisted = (r as any).persisted;
      const msg = persisted === 'local'
        ? 'Saved locally as draft — connect Etsy to publish'
        : `Created on Etsy`;
      pushToast({ kind: 'success', message: msg });
      onSaved();
      onClose();
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      kicker="// create"
      title="New Etsy listing"
      width={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} loading={saving}>
            {saving ? 'Creating…' : 'Create listing'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title" hint="Max 140 chars.">
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} className="w-full" placeholder="Hand-poured lavender soap" />
        </Field>
        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="w-full resize-none" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Price (USD)">
            <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full font-mono tabular-nums" />
          </Field>
          <Field label="Quantity">
            <input type="number" min="0" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-full font-mono tabular-nums" />
          </Field>
          <Field label="Tags (comma-separated)">
            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="handmade, gift" className="w-full" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Who made it">
            <select value={whoMade} onChange={(e) => setWhoMade(e.target.value as any)} className="w-full">
              <option value="i_did">I did</option>
              <option value="collective">A member of my shop</option>
              <option value="someone_else">Another company / person</option>
            </select>
          </Field>
          <Field label="When made">
            <select value={whenMade} onChange={(e) => setWhenMade(e.target.value as any)} className="w-full">
              <option value="made_to_order">Made to order</option>
              <option value="2020_2024">2020–2024</option>
              <option value="2010_2019">2010–2019</option>
              <option value="2000_2009">2000–2009</option>
              <option value="1990s">1990s</option>
              <option value="before_1990">Before 1990</option>
            </select>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-[12.5px] text-ink-700 cursor-pointer">
          <input type="checkbox" checked={isDigital} onChange={(e) => setIsDigital(e.target.checked)} className="rounded border-cream-300" />
          This is a digital download
        </label>
      </div>
    </Modal>
  );
}
